import type { MatchResolverContext, ResolverPlayer } from 'modules/manager/domain/placeholder';
import { ReviewGoal } from 'modules/match-video/domain/interfaces';
import { goalMinuteLabel, goalScoringTeam } from './scene-data-from-match';

/**
 * The score right after `goal`: the protocol goals up to it, in match-time order. A goal not in the list (it cannot
 * be — a clip pairs only with a protocol goal) — the final score.
 */
function scoreAfter(match: MatchResolverContext, goals: readonly ReviewGoal[], goal: ReviewGoal) {
  const index = goals.findIndex((item) => item.eventId === goal.eventId);
  if (index < 0) return { homeScore: match.match.homeScore, awayScore: match.match.awayScore };
  let homeScore = 0;
  let awayScore = 0;
  for (const item of goals.slice(0, index + 1)) {
    const team = goalScoringTeam(match, item);
    if (team === match.homeTeam) homeScore += 1;
    else if (team === match.awayTeam) awayScore += 1;
  }
  return { homeScore, awayScore };
}

/**
 * The match context of one plate. A goal plate (`VIDEO_GOAL_PLATE`) reads its goal from `goal`: the team it counts
 * for, the scorer, the minute, and the score right after it in `match.homeScore` / `awayScore`. A plate without a goal
 * (a dangerous moment, a goal clip not paired) is the match as is. Rosters are empty either way — a plate addresses
 * no player of a roster.
 *
 * `goals` — the protocol goals in match-time order (`ReviewGoalMatch.goals`).
 */
export function buildPlateContext(
  match: MatchResolverContext,
  goal: ReviewGoal | undefined,
  goals: readonly ReviewGoal[],
  players: ReadonlyMap<number, ResolverPlayer>,
): MatchResolverContext {
  const base: MatchResolverContext = { ...match, homeRoster: [], awayRoster: [], matches: undefined };
  if (!goal) return base;
  const team = goalScoringTeam(match, goal);
  if (!team) return base;

  const scorer = goal.scorerId === undefined ? undefined : players.get(goal.scorerId);
  return {
    ...base,
    match: { ...match.match, ...scoreAfter(match, goals, goal) },
    goal: { team, scorer, minute: goalMinuteLabel(goal) },
  };
}
